import React, { useRef, useState, useEffect } from 'react';
import AudioPrompt from './AudioPrompt';

export default function TapTargetQuestion({ question, value, onChange, disabled = false }) {
  const { config } = question;
  const audioPrompt = config?.audio_prompt;
  const backgroundImage = config?.background_image;
  const targets = config?.targets || [];

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Original image dimensions (assume 400x400 if not specified)
  const originalWidth = config?.image_width || 400;
  const originalHeight = config?.image_height || 400;

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [imageLoaded]);

  const handleTap = (target) => {
    if (disabled) return;
    onChange(target.label);
  };

  // Calculate scale factor for responsive targets
  const scaleX = containerSize.width / originalWidth;
  const scaleY = containerSize.height / originalHeight;

  return (
    <div className="space-y-4">
      {/* Audio prompt for young students */}
      {audioPrompt && <AudioPrompt text={audioPrompt} autoPlay />}

      {/* Tap target area */}
      <div
        ref={containerRef}
        className="relative w-full max-w-md mx-auto border-2 border-gray-200 rounded-lg overflow-hidden"
        style={{ aspectRatio: `${originalWidth} / ${originalHeight}` }}
      >
        {/* Background image */}
        {backgroundImage ? (
          <img
            src={backgroundImage}
            alt="Tap the correct answer"
            className="w-full h-full object-contain"
            onLoad={() => setImageLoaded(true)}
          />
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}

        {/* Tap targets */}
        {targets.map((target, index) => {
          const isSelected = value === target.label;
          const scaledX = target.x * scaleX;
          const scaledY = target.y * scaleY;
          const scaledWidth = target.width * scaleX;
          const scaledHeight = target.height * scaleY;

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleTap(target)}
              disabled={disabled}
              className={`absolute rounded-lg transition-all ${
                isSelected
                  ? 'bg-indigo-500 bg-opacity-40 border-4 border-indigo-500'
                  : 'bg-transparent hover:bg-gray-500 hover:bg-opacity-20 border-2 border-transparent'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                left: `${scaledX}px`,
                top: `${scaledY}px`,
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
              }}
              aria-label={`Select ${target.label}`}
            >
              {/* Show checkmark when selected */}
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selection feedback */}
      {value && (
        <p className="text-center text-sm text-gray-600">
          You selected: <span className="font-medium text-indigo-600">{value}</span>
        </p>
      )}

      {/* Accessibility */}
      <div className="sr-only">
        Tap on the image to select your answer.
        {targets.map((target, index) => (
          <span key={index}>Target {index + 1}: {target.label}. </span>
        ))}
      </div>
    </div>
  );
}
